import { useState } from "react";

type Role = "buyer" | "seller" | "supplier";
type Mode = "login" | "signup";

interface Props {
  defaultRole?: Role;
  onClose: () => void;
  onSuccess: (role: Role, name: string) => void;
}

const roleConfig: Record<Role, { label: string; icon: string; color: string; desc: string }> = {
  buyer: {
    label: "Buyer",
    icon: "ri-shopping-bag-3-line",
    color: "text-amber-500",
    desc: "Browse & order products",
  },
  seller: {
    label: "Seller",
    icon: "ri-store-2-line",
    color: "text-emerald-500",
    desc: "Resell & earn profits",
  },
  supplier: {
    label: "Supplier",
    icon: "ri-building-2-line",
    color: "text-sky-500",
    desc: "Upload & supply products",
  },
};

export default function DropshippingAuthModal({ defaultRole = "buyer", onClose, onSuccess }: Props) {
  const [role, setRole] = useState<Role>(defaultRole);
  const [mode, setMode] = useState<Mode>("signup");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    company: "",
    country: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setTimeout(() => {
        onSuccess(role, form.name || "User");
      }, 1200);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <h2 className="text-white font-bold text-xl">
            {mode === "signup" ? "Join Crown Dropshipping" : "Welcome Back"}
          </h2>
          <p className="text-white/80 text-sm mt-0.5">
            {mode === "signup" ? "Create your account to get started" : "Sign in to your account"}
          </p>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                <i className="ri-check-line text-green-500 text-3xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-1">
                {mode === "signup" ? "Account Created!" : "Logged In!"}
              </h3>
              <p className="text-gray-500 text-sm">
                Welcome, {form.name || "User"}! Redirecting to your dashboard...
              </p>
            </div>
          ) : (
            <>
              {/* Role Selector */}
              <div className="mb-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">I am a...</p>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.keys(roleConfig) as Role[]).map((r) => {
                    const cfg = roleConfig[r];
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                          role === r
                            ? "border-amber-400 bg-amber-50"
                            : "border-stone-200 hover:border-stone-300"
                        }`}
                      >
                        <div className={`w-8 h-8 flex items-center justify-center`}>
                          <i className={`${cfg.icon} text-xl ${role === r ? cfg.color : "text-stone-400"}`}></i>
                        </div>
                        <span className={`text-xs font-semibold ${role === r ? "text-amber-600" : "text-stone-500"}`}>
                          {cfg.label}
                        </span>
                        <span className="text-xs text-stone-400 text-center leading-tight">{cfg.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Mode Toggle */}
              <div className="flex bg-stone-100 rounded-lg p-1 mb-5">
                {(["signup", "login"] as Mode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={`flex-1 py-2 text-sm font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      mode === m ? "bg-white text-gray-900 shadow-sm" : "text-stone-500 hover:text-gray-700"
                    }`}
                  >
                    {m === "signup" ? "Sign Up" : "Log In"}
                  </button>
                ))}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email Address</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Password</label>
                  <input
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>

                {mode === "signup" && (role === "seller" || role === "supplier") && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Company / Business Name</label>
                      <input
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Country</label>
                      <select
                        name="country"
                        value={form.country}
                        onChange={handleChange}
                        className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer"
                      >
                        <option value="">Select country</option>
                        <option>United States</option>
                        <option>Canada</option>
                        <option>United Kingdom</option>
                        <option>Australia</option>
                        <option>China</option>
                        <option>Hong Kong</option>
                        <option>Singapore</option>
                        <option>Germany</option>
                        <option>France</option>
                        <option>Japan</option>
                        <option>South Korea</option>
                        <option>India</option>
                        <option>UAE</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </>
                )}

                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm mt-2 disabled:opacity-60"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      {mode === "signup" ? "Creating Account..." : "Signing In..."}
                    </span>
                  ) : (
                    mode === "signup" ? `Create ${roleConfig[role].label} Account` : "Sign In"
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
